import {CancelToken} from 'axios';
import {Effect, Schedule, Duration} from 'effect';

import {operationsService} from 'api/operations';
import {tasksService} from 'api/tasks';
import {SortOrder} from 'shared/constants/common';
import {SortField, TaskProjection} from 'shared/models/task/const';
import {TaskFilterQuery, TaskStatusQuery} from 'shared/models/task/filter';
import {OperationModelRawDTO, TaskDetailsModelDTO, TaskModelRawDTO} from 'shared/models/task/task';

import {ApiResponse} from '../BatchLoader';
import {EffectError, handleInteruptableErrors, handleRequestWithCancellation} from '../errors';

const RetryPolicy = Schedule.exponential(Duration.seconds(2)).pipe(Schedule.compose(Schedule.recurs(3)));

type FetchFunction<T> = (
  offset: number,
  take: number,
  cancelToken: CancelToken,
) => Effect.Effect<ApiResponse<T>, EffectError, never>;

export const createFetchTaskDetailByDateBatch = (
  params: Partial<TaskFilterQuery & TaskStatusQuery>,
  projection: TaskProjection,
): FetchFunction<TaskDetailsModelDTO> => {
  return (offset: number, take: number, cancelToken: CancelToken) =>
    Effect.tryPromise({
      try: () =>
        tasksService.getProjectTasks({
          projection,
          includeSummaryTasks: true,
          offset,
          limit: take,
          params,
          sortOrder: SortOrder.ASC,
          sortField: SortField.outlineSortKey,
          cancelToken,
        }),
      catch: handleInteruptableErrors,
    }).pipe(
      Effect.map((response) => ({
        data: response.data as unknown as TaskDetailsModelDTO[],
        headers: response.headers,
      })),
      Effect.retry(RetryPolicy),
    );
};

export const createAllTasksFetchBatch = ({
  filterParams,
}: {
  filterParams: Partial<TaskFilterQuery & TaskStatusQuery>;
}): FetchFunction<TaskModelRawDTO> => {
  return (offset: number, take: number, cancelToken: CancelToken) =>
    Effect.tryPromise({
      try: () =>
        tasksService.getV2ProjectTasks({
          offset,
          limit: take,
          params: filterParams,
          sortOrder: SortOrder.ASC,
          sortField: SortField.outlineSortKey,
          cancelToken,
        }),
      catch: handleInteruptableErrors,
    }).pipe(
      Effect.map((response) => {
        const allTasks = response.data.tasks;
        const summaryTasks = response?.data?.summary_tasks ?? [];
        allTasks.push(...summaryTasks);

        return {
          data: allTasks as TaskModelRawDTO[],
          headers: response.headers,
        };
      }),
    );
};

export const createOperationIdsFetcherEffect = (projectId: string) => {
  return (
    offset: number,
    take: number,
    cancelToken: CancelToken,
  ): Effect.Effect<ApiResponse<OperationModelRawDTO>, EffectError, never> =>
    Effect.tryPromise({
      try: () =>
        operationsService.getOperations(projectId, {
          offset,
          limit: take,
          sortOrder: SortOrder.DESC,
          sortField: SortField.outlineSortKey,
          cancelToken,
        }),
      catch: handleRequestWithCancellation,
    }).pipe(
      Effect.map((response) => ({
        data: response.data as OperationModelRawDTO[],
        headers: response.headers,
      })),
    );
};
